/**
 * Sistema de Detecção de Plataforma
 * Implementação específica para detecção de navegador e dispositivo
 */

import { 
  IPlatformDetectionService, 
  PlatformInfo, 
  PlatformType, 
  BrowserType 
} from '../interfaces/platform_detection_service';
import { ServiceStatus, ServiceCapabilities } from '../interfaces/base_platform_service';

export class PlatformDetectionSystem implements IPlatformDetectionService {
  readonly serviceName = 'PlatformDetection' as const;
  private _isInitialized = false;
  private _platformInfo: PlatformInfo | null = null;

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  async initialize(): Promise<void> {
    if (this._isInitialized) return;

    console.log('🔍 Inicializando sistema de detecção de plataforma...');
    
    this._platformInfo = this.detectPlatform();
    this._isInitialized = true;

    console.log('✅ Plataforma detectada:', {
      type: this._platformInfo.type,
      browser: this._platformInfo.browser,
      version: this._platformInfo.version,
      isMobile: this._platformInfo.isMobile
    });
  }

  cleanup(): void {
    this._platformInfo = null;
    this._isInitialized = false;
  }

  getStatus(): ServiceStatus {
    return {
      isAvailable: true,
      isInitialized: this._isInitialized,
      lastUpdate: new Date(),
      metadata: {
        platformType: this._platformInfo?.type,
        browser: this._platformInfo?.browser,
        version: this._platformInfo?.version
      }
    };
  }

  getPlatformInfo(): PlatformInfo {
    if (!this._platformInfo) {
      throw new Error('Platform detection not initialized');
    }
    return this._platformInfo;
  }

  refreshPlatformInfo(): void {
    this._platformInfo = this.detectPlatform();
  }

  isBrowser(browser: BrowserType): boolean {
    return this.getPlatformInfo().browser === browser;
  }

  isPlatformType(type: PlatformType): boolean {
    return this.getPlatformInfo().type === type;
  }

  getCapabilities(): ServiceCapabilities {
    const info = this.getPlatformInfo();
    return info.capabilities;
  }

  private detectPlatform(): PlatformInfo {
    const userAgent = navigator.userAgent;
    
    const capabilities = this.detectCapabilities();
    
    return {
      type: this.detectPlatformType(),
      browser: this.detectBrowser(userAgent),
      version: this.detectVersion(userAgent),
      isMobile: this.isMobile(userAgent),
      isTablet: this.isTablet(userAgent),
      isDesktop: this.isDesktop(userAgent),
      userAgent,
      capabilities
    };
  }

  private detectPlatformType(): PlatformType {
    const userAgent = navigator.userAgent;

    // Electron
    if (userAgent.includes('Electron')) {
      return PlatformType.ELECTRON;
    }

    // PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return PlatformType.PWA;
    }

    // WebView
    if (userAgent.includes('wv') || userAgent.includes('WebView')) {
      return PlatformType.WEBVIEW;
    }

    // Mobile Web
    if (this.isMobile(userAgent)) {
      return PlatformType.MOBILE_WEB;
    }

    return PlatformType.WEB;
  }

  private detectBrowser(userAgent: string): BrowserType {
    if (userAgent.includes('Edg/')) return BrowserType.EDGE;
    if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return BrowserType.CHROME;
    if (userAgent.includes('Firefox/')) return BrowserType.FIREFOX;
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return BrowserType.SAFARI;
    if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) return BrowserType.OPERA;
    if (userAgent.includes('SamsungBrowser/')) return BrowserType.SAMSUNG;
    return BrowserType.UNKNOWN;
  }

  private detectVersion(userAgent: string): string {
    const matches = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/([0-9.]+)/);
    return matches ? matches[2] : 'unknown';
  }

  private isMobile(userAgent: string): boolean {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  }

  private isTablet(userAgent: string): boolean {
    return /iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent);
  }

  private isDesktop(userAgent: string): boolean {
    return !this.isMobile(userAgent) && !this.isTablet(userAgent);
  }

  private detectCapabilities(): ServiceCapabilities {
    return {
      // Service Worker
      serviceWorker: 'serviceWorker' in navigator,
      
      // Push Notifications
      pushNotifications: 'PushManager' in window && 'serviceWorker' in navigator,
      
      // Web Share
      webShare: 'share' in navigator,
      
      // Fullscreen
      fullscreen: 'requestFullscreen' in document.documentElement,
      
      // Vibration
      vibration: 'vibrate' in navigator,
      
      // Geolocation
      geolocation: 'geolocation' in navigator,
      
      // Media Devices
      mediaDevices: 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
      
      // File System Access
      fileSystem: 'showOpenFilePicker' in window,
      
      // Battery (deprecated)
      battery: 'getBattery' in navigator,
      
      // Network Information
      networkInformation: 'connection' in navigator,
      
      // Device Orientation
      deviceOrientation: 'DeviceOrientationEvent' in window,
      
      // Clipboard
      clipboard: 'clipboard' in navigator,
      
      // Wake Lock
      wakeLock: 'wakeLock' in navigator,
      
      // Web Locks
      webLocks: 'locks' in navigator,
      
      // Observers
      intersectionObserver: 'IntersectionObserver' in window,
      resizeObserver: 'ResizeObserver' in window,
      mutationObserver: 'MutationObserver' in window,
      performanceObserver: 'PerformanceObserver' in window,
      
      // Storage
      localStorage: typeof Storage !== 'undefined' && 'localStorage' in window,
      sessionStorage: typeof Storage !== 'undefined' && 'sessionStorage' in window,
      indexedDB: 'indexedDB' in window,
      caches: 'caches' in window
    };
  }
}
